import { observer } from 'mobx-react'
import type { FC } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { PIXEL_RATIO } from '../constants'
import type { PanEvent } from '../hooks'
import { POINTER_TYPE, useGestureEvent } from '../hooks'
import { errorHandlerService } from '../services/errorHandler.service'
import type { Size } from '../types'
import type {
  HandwrittenNoteReadFunc,
  HandwrittenNoteWriteFunc,
  NoteData,
} from './handwrittenNote.types'
import HandwrittenNoteEraserIndicator from './HandwrittenNoteEraserIndicator'
import HandwrittenNotePath from './HandwrittenNotePath'
import { MIN_TEXT_BOX_HEIGHT, MIN_TEXT_BOX_WIDTH } from './HandwrittenNoteTextBox'
import HandwrittenNoteService from './service/HandwrittenNote.service'
import type HandwrittenNoteControllerService from './service/HandwrittenNoteController.service'
import HandwrittenNotePathItem from './service/HandwrittenNotePathItem'

interface Props {
  readFunc?: HandwrittenNoteReadFunc
  writeFunc?: HandwrittenNoteWriteFunc
  viewBoxSize: Size
  controllerService?: HandwrittenNoteControllerService
  saveDelay?: number
  noteData?: NoteData
  readonlyNoteData?: NoteData
  renderPriority?: 'readonlyNoteData' | 'noteData'
}

const HandwrittenNote: FC<Props> = ({
  readFunc,
  writeFunc,
  viewBoxSize,
  controllerService,
  saveDelay,
  noteData,
  readonlyNoteData,
  renderPriority = 'noteData',
}) => {
  const serviceRef = useRef(new HandwrittenNoteService(controllerService))
  const service = serviceRef.current
  const { isWritingMode, needExtraFinger, activeTool } = service.controllerService

  useEffect(() => {
    service.controllerService.setNoteService(service)
  }, [service])

  useEffect(() => {
    service.reset()

    if (readFunc) {
      readFunc()
        .then((noteData) => {
          if (noteData) {
            service.noteData.fromJSON(noteData)
          }
        })
        .catch(errorHandlerService.handle)
    }
  }, [readFunc, service])

  useEffect(() => {
    service.reset()

    if (noteData && !readFunc) {
      service.noteData.fromJSON(noteData)
    }
  }, [noteData, service])

  useEffect(() => {
    service.setViewBoxSize(viewBoxSize)
  }, [service, viewBoxSize])

  useEffect(() => {
    if (writeFunc) {
      service.setWriterFunc(writeFunc)
    }
  }, [service, writeFunc])

  const handlePanStart = useCallback(
    (e: PanEvent) => {
      if (!isWritingMode) {
        return
      }
      if (!needExtraFinger && e.pointerType === POINTER_TYPE.touch) {
        return
      }
      if (activeTool === 'text') {
        return
      }
      service.handlePanStart(e)
    },
    [needExtraFinger, isWritingMode, activeTool, service],
  )

  const handlePan = useCallback(
    (e: PanEvent) => {
      if (!isWritingMode) {
        return
      }
      if (!needExtraFinger && e.pointerType === POINTER_TYPE.touch) {
        return
      }
      service.handlePan(e)
    },
    [needExtraFinger, isWritingMode, service],
  )

  const handlePanEnd = useCallback(
    (e: PanEvent) => {
      if (!isWritingMode) {
        return
      }
      if (!needExtraFinger && e.pointerType === POINTER_TYPE.touch) {
        return
      }
      service.handlePanEnd()
    },
    [needExtraFinger, isWritingMode, service],
  )

  const gestureEventOptions = useMemo(() => {
    return {
      usePointerCapture: true,
      minMoveDistance: 0,
      moveDelay: 0,
      preventDefaultTouchMove: true,
      onPanStart: handlePanStart,
      onPan: handlePan,
      onPanEnd: handlePanEnd,
    }
  }, [handlePan, handlePanEnd, handlePanStart])

  const { gestureRef } = useGestureEvent<SVGSVGElement>(gestureEventOptions)
  const viewBoxSizeString = `0 0 ${viewBoxSize.width * PIXEL_RATIO} ${viewBoxSize.height * PIXEL_RATIO}`

  const handleAutoToolbarHide = useCallback(() => {
    service.controllerService.setToolbarMode('simple')
  }, [service.controllerService])

  useEffect(() => {
    if (service.isModified) {
      service.requestSave(saveDelay).catch(errorHandlerService.handle)
    }
  }, [saveDelay, service, service.isModified])

  const readonlyPathItems = useMemo(() => {
    if (!readonlyNoteData) {
      return []
    }
    return readonlyNoteData.paths.map(HandwrittenNotePathItem.fromData)
  }, [readonlyNoteData])

  return (
    <>
      {readonlyNoteData && (
        <>
          <svg
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              display: service.controllerService.isHiddenNote ? 'none' : 'unset',
              zIndex: renderPriority === 'readonlyNoteData' ? 1 : 0,
            }}
            viewBox={`0 0 ${readonlyNoteData.viewBox[0] * PIXEL_RATIO} ${readonlyNoteData.viewBox[1] * PIXEL_RATIO}`}
          >
            {readonlyPathItems.map((item) => (
              <HandwrittenNotePath key={item.id} item={item} service={service} />
            ))}
          </svg>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: service.controllerService.isHiddenNote ? 'none' : 'unset',
            }}
          >
            {readonlyNoteData.textItems?.map((item) => {
              const scaleX = viewBoxSize.width / readonlyNoteData.viewBox[0]
              const scaleY = viewBoxSize.height / readonlyNoteData.viewBox[1]
              const width = (item.width || MIN_TEXT_BOX_WIDTH) * scaleX
              const height = (item.height || MIN_TEXT_BOX_HEIGHT) * scaleY
              return (
                <div
                  key={item.id}
                  style={{
                    position: 'absolute',
                    left: item.x * scaleX,
                    top: item.y * scaleY,
                    width: width,
                    height: height,
                    minWidth: MIN_TEXT_BOX_WIDTH * scaleX,
                    minHeight: MIN_TEXT_BOX_HEIGHT * scaleY,
                    color: item.color,
                    fontSize: item.fontSize * Math.min(scaleX, scaleY),
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    padding: '18px 24px',
                    boxSizing: 'border-box',
                    overflow: 'visible',
                    display: 'flex',
                    alignItems: 'flex-start',
                    lineHeight: 1.2,
                  }}
                >
                  {item.text}
                </div>
              )
            })}
          </div>
        </>
      )}
      <svg
        ref={gestureRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          touchAction: 'none',
          display: service.controllerService.isHiddenNote ? 'none' : 'unset',
          width: `${PIXEL_RATIO * 100}%`,
          height: `${PIXEL_RATIO * 100}%`,
          transform: `scale(${1 / PIXEL_RATIO})`,
          transformOrigin: '0 0',
        }}
        viewBox={viewBoxSizeString}
        onPointerDown={handleAutoToolbarHide}
      >
        {service.noteData.visiblePathItems.map((item) => (
          <HandwrittenNotePath key={item.id} item={item} service={service} />
        ))}
      </svg>
      <HandwrittenNoteEraserIndicator service={service} />
    </>
  )
}

export default observer(HandwrittenNote)
